import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';

const STATUS_STYLE = {
  UNPAID: { color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5' },
  PAID:   { color: '#16a34a', background: '#f0fdf4', border: '1px solid #86efac' },
  VOID:   { color: '#6b7280', background: '#f3f4f6', border: '1px solid #d1d5db' },
};

export default function BillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [id]);

  async function load() {
    const [{ data: b }, { data: pvs }] = await Promise.all([
      supabase.from('bills')
        .select('*, expense_account:expense_account_id(account_name, account_code)')
        .eq('id', id).single(),
      supabase.from('payment_vouchers')
        .select('id, voucher_number, voucher_date, amount, payment_mode')
        .eq('reference_type', 'bill')
        .eq('reference_id', id),
    ]);
    setBill(b);
    setPayments(pvs || []);
    setLoading(false);
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" /></div>;
  if (!bill) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Bill not found.</div>;

  const totalPaid = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const balance = parseFloat(bill.amount) - totalPaid;
  const overdue = bill.status === 'UNPAID' && bill.due_date && bill.due_date < new Date().toISOString().slice(0, 10);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Action bar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 24 }}>
        <button className="btn btn-secondary" onClick={() => navigate('/bills')}>← Bills</button>
        <div style={{ flex: 1 }} />
        {bill.status === 'UNPAID' && (
          <button className="btn btn-primary"
            onClick={() => navigate(`/vouchers/payment/new?bill_id=${id}&payee=${encodeURIComponent(bill.payee)}&amount=${balance}&desc=${encodeURIComponent(`Bill payment — ${bill.payee}${bill.expense_account?.account_name ? ' (' + bill.expense_account.account_name + ')' : ''}`)}`)}>
            Pay This Bill
          </button>
        )}
        <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
      </div>

      <div className="card">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary)' }}>{bill.bill_number}</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{bill.payee}</div>
            {bill.reference && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Ref: {bill.reference}</div>}
          </div>
          <span style={{ ...STATUS_STYLE[bill.status], padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
            {bill.status}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Expense Account</div>
            <div style={{ fontWeight: 600 }}>{bill.expense_account?.account_code} — {bill.expense_account?.account_name}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Bill Date</div>
            <div style={{ fontWeight: 600 }}>{bill.bill_date}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Due Date</div>
            <div style={{ fontWeight: 600, color: overdue ? '#dc2626' : undefined }}>
              {bill.due_date || '—'}{overdue ? ' ⚠ Overdue' : ''}
            </div>
          </div>
          {bill.description && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Notes</div>
              <div>{bill.description}</div>
            </div>
          )}
        </div>

        {/* Amount summary */}
        <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
            <span>Bill Amount</span>
            <span style={{ fontWeight: 600 }}>LKR {parseFloat(bill.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          {totalPaid > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, color: '#16a34a' }}>
              <span>Amount Paid</span>
              <span style={{ fontWeight: 600 }}>LKR {totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 16, fontWeight: 700, color: balance > 0 ? '#dc2626' : '#16a34a' }}>
            <span>Balance Due</span>
            <span>LKR {balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Payment history */}
        {payments.length > 0 && (
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Payments</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Voucher</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Mode</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => navigate(`/vouchers/payment/${p.id}`)}>
                    <td style={{ padding: '7px 8px', color: 'var(--primary)', fontWeight: 600 }}>{p.voucher_number}</td>
                    <td style={{ padding: '7px 8px' }}>{p.voucher_date}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--text-muted)' }}>{p.payment_mode}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600 }}>LKR {parseFloat(p.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Double entry info */}
        <div style={{ marginTop: 20, padding: '10px 14px', background: '#f8fafc', borderRadius: 6, fontSize: 12, color: 'var(--text-muted)' }}>
          <strong>Journal posted on entry:</strong> DR {bill.expense_account?.account_name} / CR Other Payables (2060)
          {payments.length > 0 && <><br /><strong>On payment:</strong> DR Other Payables (2060) / CR Bank / Cash</>}
        </div>
      </div>
    </div>
  );
}
